import React, { type JSX } from 'react';
import useConfirmationDialogBox from 'utils/useConfirmationDialogBox';
import useContractData from 'utils/hooks/useContractData';
import { useGetStatusOptionsQuery } from 'services/api/contracts';
import { statusChangeMessage } from 'helper/companies';

type SubmitHandler = (event: React.FormEvent<HTMLFormElement>) => void;

/**
 * Intercepta o submit do formulário de empresa e, quando o Status do contrato
 * foi alterado, abre um modal de confirmação antes de salvar. Se o status não
 * mudou (ou é uma empresa nova, sem status original) salva direto. Formulário
 * inválido segue o fluxo normal de salvar (que marca os erros), sem confirmação.
 */
export default function useStatusChangeConfirmation(
  contractId: number,
  onConfirmedSubmit: SubmitHandler,
): { guardedSubmit: SubmitHandler, statusConfirmationDialogBox: JSX.Element } {
  const { contractData } = useContractData(contractId);
  const { data: statusOptions } = useGetStatusOptionsQuery();
  const { confirmationDialogBox, setConfirmationDialogBox } = useConfirmationDialogBox();

  const originalStatusId = contractData?.statusId;
  const labelForStatus = (id: number | undefined): string => (
    statusOptions?.find((option) => option.id === id)?.label ?? ''
  );

  const guardedSubmit: SubmitHandler = (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;

    // Formulário inválido: deixa o save normal marcar os erros, sem confirmação.
    if (!form.checkValidity()) {
      onConfirmedSubmit(event);
      return;
    }

    const newStatusId = Number(new FormData(form).get('status'));
    const statusChanged = originalStatusId !== undefined
      && Number.isFinite(newStatusId)
      && newStatusId !== originalStatusId;

    if (statusChanged) {
      setConfirmationDialogBox(
        true,
        statusChangeMessage(labelForStatus(originalStatusId), labelForStatus(newStatusId)),
        () => onConfirmedSubmit(event),
      );
      return;
    }

    onConfirmedSubmit(event);
  };

  return { guardedSubmit, statusConfirmationDialogBox: confirmationDialogBox };
}
